"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { STATIC_ASSETS } from "@/lib/cdn";
import { useAuth } from "@/hooks/useAuth";
import { usePrivy } from "@privy-io/react-auth";
import { useUserMe } from "@/contexts/UserMeContext";
import { FiSend, FiLink, FiUsers, FiGlobe, FiChevronDown } from "react-icons/fi";
import { XIcon } from "./icons/XIcon";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import { usePoints } from "@/contexts/PointsContext";
import LoginPromptModal from "./LoginPromptModal";
import {
  ALLOWED_DOMAINS_HUMAN,
  extractUrls,
  isAllowedUrl,
  isMarketUrl,
  resolvePostLinkPreview,
  extractTraderRef,
  type LinkPreview,
  type MarketPreview,
} from "@/lib/market-url-preview";
import TraderCardCompact from "@/components/traders/TraderCardCompact";
import { useT } from "@/hooks/useT";
import { formatCents } from '@/lib/format-cents';

type PitOption = {
  id: string;
  slug: string;
  name: string;
  visibility: 'public' | 'private';
  viewerStatus: 'active' | 'pending' | 'banned' | null;
};

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMarketPreview?: MarketPreview | null;
}

function formatVolume(volume: number | undefined): string {
  if (!volume) return "$0";
  if (volume >= 1000000) return `$${(volume / 1000000).toFixed(1)}M`;
  if (volume >= 1000) return `$${(volume / 1000).toFixed(1)}K`;
  return `$${volume.toFixed(0)}`;
}

export default function CreatePostModal({ isOpen, onClose, initialMarketPreview }: CreatePostModalProps) {
  const t = useT();
  const { isLoggedIn } = useAuth();
  const { user, login } = usePrivy();
  const { user: userMe } = useUserMe();
  const router = useRouter();
  const { triggerAnimation } = usePoints();

  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [urlError, setUrlError] = useState("");
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const postButtonRef = useRef<HTMLButtonElement>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [userDismissedPreview, setUserDismissedPreview] = useState(false);
  const lastInitialPreviewId = useRef<string | null>(null);
  // Convenience accessor — most existing UI checks talk about a market.
  const marketPreview = linkPreview?.kind === "market" ? linkPreview : null;

  // Pit selector: lista de pits donde el user puede postear (status='active')
  const [myPits, setMyGroups] = useState<PitOption[]>([]);
  const [selectedPitId, setSelectedPitId] = useState<string | null>(null);
  const [pitPickerOpen, setPitPickerOpen] = useState(false);

  // FrenFlow user profile from UserMe context
  const frenflowProfile = userMe ? {
    username: userMe.username ?? null,
    name: userMe.name ?? null,
    image: userMe.image ?? null,
  } : null;

  // Set initial market preview when modal opens with one
  useEffect(() => {
    // If the initial preview changed (different market), reset dismissed state
    const currentId = initialMarketPreview?.id || null;
    if (currentId !== lastInitialPreviewId.current) {
      lastInitialPreviewId.current = currentId;
      setUserDismissedPreview(false);
      setHasInitialized(false);
    }

    if (isOpen && initialMarketPreview && !hasInitialized && !userDismissedPreview) {
      setLinkPreview({ kind: "market", ...initialMarketPreview });
      setHasInitialized(true);
    }
  }, [isOpen, initialMarketPreview, hasInitialized, userDismissedPreview]);

  // Fetch pits del usuario al abrir modal
  useEffect(() => {
    if (!isOpen || !isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/groups/mine', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const active: PitOption[] = (data.pits || [])
          .filter((g: any) => g.viewerStatus === 'active')
          .map((g: any) => ({
            id: g.id,
            slug: g.slug,
            name: g.name,
            visibility: g.visibility,
            viewerStatus: g.viewerStatus,
          }));
        setMyGroups(active);
      } catch {
        /* silent — no es bloqueante */
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isLoggedIn]);

  // Handle user dismissing the preview
  const handleDismissPreview = () => {
    setLinkPreview(null);
    setUserDismissedPreview(true);
  };

  const handleContentChange = useCallback(async (newContent: string) => {
    setContent(newContent);
    setUrlError("");

    const urls = extractUrls(newContent);

    const disallowedUrls = urls.filter(url => !isAllowedUrl(url));
    if (disallowedUrls.length > 0) {
      setUrlError(`Only links to ${ALLOWED_DOMAINS_HUMAN} are allowed`);
      return;
    }

    // Dedupe vs current preview so we don't refetch on every keystroke.
    const marketUrl = urls.find(url => isMarketUrl(url));
    const traderRef = extractTraderRef(newContent);
    if (
      linkPreview?.kind === "market" &&
      marketUrl &&
      linkPreview.url === marketUrl
    ) return;
    if (
      linkPreview?.kind === "trader" &&
      traderRef &&
      linkPreview.url === traderRef.matched
    ) return;

    if (!marketUrl && !traderRef) return; // no-op, keep last preview
    setLoadingPreview(true);
    try {
      const preview = await resolvePostLinkPreview(newContent);
      if (preview) {
        setLinkPreview(preview);
        setUserDismissedPreview(false);
      }
    } finally {
      setLoadingPreview(false);
    }
    // Don't clear preview when typing - only clear when user explicitly dismisses it
  }, [linkPreview]);

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (!content.trim() || posting || urlError) return;

    setPosting(true);
    try {
      const response = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          content: content.trim(),
          linkPreview,
          groupId: selectedPitId,
        }),
      });

      if (response.ok) {
        // Backend awards 0.5 FP for a post that contains a polymarket.com
        // or frenflow.com link (see fren-points.ts → awardPostFP). No
        // link → 0 FP. Match that exactly so the animation isn't lying.
        const hasValidLink =
          content.toLowerCase().includes("polymarket.com") ||
          content.toLowerCase().includes("frenflow.com");
        if (hasValidLink && postButtonRef.current) {
          const rect = postButtonRef.current.getBoundingClientRect();
          triggerAnimation(0.5, rect.left + rect.width / 2, rect.top + rect.height / 2);
        }

        // Reset form
        setContent("");
        setLinkPreview(null);
        const postedToPitSlug = selectedPit?.slug || null;
        setSelectedPitId(null);
        setPitPickerOpen(false);
        onClose();

        // Navigate: si fue a un grupo, ir al grupo; si no, al feed global de talks
        router.push(postedToPitSlug ? `/groups/${postedToPitSlug}` : "/talks");
      } else {
        const error = await response.json();
        alert(error.error || "Failed to create post");
      }
    } catch (error) {
      alert("Failed to create post");
    } finally {
      setPosting(false);
    }
  };

  const handleClose = () => {
    setContent("");
    setLinkPreview(null);
    setUrlError("");
    setSelectedPitId(null);
    setPitPickerOpen(false);
    onClose();
  };

  const selectedPit = selectedPitId ? myPits.find(g => g.id === selectedPitId) : null;

  // Get username for greeting - FrenFlow profile takes priority
  const username = frenflowProfile?.username ||
                   frenflowProfile?.name?.split(' ')[0] ||
                   (user as any)?.twitter?.username ||
                   (user as any)?.google?.name?.split(' ')[0] ||
                   (user as any)?.email?.address?.split('@')[0] ||
                   'fren';

  // Get avatar - FrenFlow profile takes priority
  const userAvatar = frenflowProfile?.image ||
                     (user as any)?.google?.profilePictureUrl ||
                     (user as any)?.twitter?.profilePictureUrl ||
                     STATIC_ASSETS.defaultAvatar;

  return (
    <>
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="Sign in to create a post"
      />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[500] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "100%", opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="w-full sm:max-w-lg bg-white dark:bg-[#18181D] rounded-t-[5px] sm:rounded-[5px] shadow-2xl border border-gray-200 dark:border-[#1A1A1A] flex flex-col h-[100dvh] sm:h-auto sm:max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Not logged in state */}
              {!isLoggedIn ? (
                <div className="p-6 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center">
                    <FiSend className="w-8 h-8 text-gray-400" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    Join the conversation
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Sign in to share your predictions and alpha with the pit
                  </p>
                  <button
                    onClick={() => {
                      handleClose();
                      login();
                    }}
                    className="w-full py-3 bg-[#7C3AED] dark:bg-[#CCFF00] text-white dark:text-[#0A0A0A] font-bold rounded-[5px] hover:opacity-90 transition-colors"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleClose}
                    className="w-full mt-3 py-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  >
                    Maybe later
                  </button>
                </div>
              ) : (
              <>
              {/* Header - Friendly greeting with avatar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-[#1A1A1A]">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-[5px] bg-gray-200 dark:bg-[#1A1A1A] p-0.5 flex-shrink-0">
                    <Image
                      src={userAvatar}
                      alt={t("createPost.yourAvatar")}
                      width={36}
                      height={36}
                      sizes="36px"
                      className="w-full h-full rounded-[5px] object-cover"
                    />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                      {t("createPost.greeting", { name: username })}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {marketPreview ? t("createPost.shareYourTake") : t("createPost.whatsYourPrediction")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-1.5 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#0f0f12] transition-colors"
                >
                  <XIcon size={20} className="text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <form onSubmit={handleCreatePost} className="p-4 flex-1 overflow-y-auto">
                <div>
                  {/* Audience picker — feed global o uno de mis pits */}
                  {myPits.length > 0 && (
                    <div className="relative mb-3">
                      <button
                        type="button"
                        onClick={() => setPitPickerOpen(o => !o)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#0f0f12] text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#18181D]"
                      >
                        {selectedPit ? (
                          <>
                            <FiUsers className="w-3 h-3" />
                            <span className="font-medium">{selectedPit.name}</span>
                          </>
                        ) : (
                          <>
                            <FiGlobe className="w-3 h-3" />
                            <span>{t("createPost.publicFeed")}</span>
                          </>
                        )}
                        <FiChevronDown className="w-3 h-3 opacity-60" />
                      </button>

                      {pitPickerOpen && (
                        <>
                          <button
                            type="button"
                            aria-label="close"
                            onClick={() => setPitPickerOpen(false)}
                            className="fixed inset-0 z-[10]"
                          />
                          <div className="absolute z-[20] left-0 mt-1 min-w-[220px] max-h-72 overflow-y-auto rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#0f0f12] shadow-lg">
                            <button
                              type="button"
                              onClick={() => { setSelectedPitId(null); setPitPickerOpen(false); }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-[#18181D] ${selectedPitId === null ? 'bg-gray-50 dark:bg-[#18181D]' : ''}`}
                            >
                              <FiGlobe className="w-3.5 h-3.5 text-gray-400" />
                              <span className="font-medium text-gray-900 dark:text-gray-100">{t("createPost.publicFeed")}</span>
                            </button>
                            <div className="my-1 border-t border-gray-200 dark:border-[#1A1A1A]" />
                            {myPits.map(g => (
                              <button
                                key={g.id}
                                type="button"
                                onClick={() => { setSelectedPitId(g.id); setPitPickerOpen(false); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-[#18181D] ${selectedPitId === g.id ? 'bg-gray-50 dark:bg-[#18181D]' : ''}`}
                              >
                                <FiUsers className="w-3.5 h-3.5 text-gray-400" />
                                <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{g.name}</span>
                                <span className="text-gray-400 text-[10px] ml-auto shrink-0">@{g.slug}</span>
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <textarea
                    value={content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    onFocus={(e) => {
                      // On mobile, scroll textarea into view when keyboard opens
                      setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                    }}
                    placeholder={marketPreview?.isEvent ? t("createPost.placeholder.event") : marketPreview ? t("createPost.placeholder.market") : t("createPost.placeholder.alpha")}
                    rows={5}
                    maxLength={500}
                    autoFocus
                    className={`w-full px-3 py-3 font-satoshi text-sm border rounded-[5px] focus-visible:ring-2 focus-visible:ring-gray-400/50 focus-visible:outline-none resize-none bg-gray-50 dark:bg-[#0f0f12] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors min-h-[120px] ${
                      urlError ? "border-[#FF1744]" : "border-gray-200 dark:border-[#1A1A1A]"
                    }`}
                  />

                  {/* Helper hint */}
                  {!marketPreview && !loadingPreview && (
                    <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                      <FiLink className="w-3 h-3" />
                      {t("createPost.pasteHint")}
                    </p>
                  )}

                  {/* URL Error */}
                  {urlError && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-red-600">
                      <FiLink className="w-3.5 h-3.5" />
                      {urlError}
                    </div>
                  )}

                  {/* Loading Preview */}
                  {loadingPreview && (
                    <div className="mt-2 p-2.5 border border-gray-200 rounded-[5px] animate-pulse dark:border-gray-700">
                      <div className="flex gap-2.5">
                        <div className="w-10 h-10 bg-gray-200 rounded-[5px] dark:bg-gray-700 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded w-3/4 mb-1.5 dark:bg-gray-700" />
                          <div className="h-2.5 bg-gray-200 rounded w-1/2 dark:bg-gray-700" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Trader Preview */}
                  {linkPreview?.kind === "trader" && !loadingPreview && (
                    <TraderCardCompact
                      trader={linkPreview}
                      onDismiss={handleDismissPreview}
                    />
                  )}

                  {/* Market/Event Preview - Compact */}
                  {marketPreview && !loadingPreview && (
                    <div className="mt-2 p-2.5 border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] bg-gray-50 dark:bg-[#0f0f12]">
                      <div className="flex items-start justify-between mb-1.5">
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {marketPreview.isEvent
                            ? `Event · ${marketPreview.marketsCount ?? marketPreview.markets?.length ?? "?"} markets`
                            : "Market preview"}
                        </span>
                        <button
                          type="button"
                          onClick={handleDismissPreview}
                          className="p-0.5 text-gray-400 hover:text-gray-600 rounded flex-shrink-0"
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                      <div className="flex items-start gap-2.5">
                        {marketPreview.image && (
                          <Image
                            src={marketPreview.image}
                            alt={marketPreview.question || marketPreview.title || ""}
                            width={40}
                            height={40}
                            className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-xs line-clamp-2 dark:text-white leading-tight">
                            {marketPreview.question || marketPreview.title}
                          </h4>
                          {marketPreview.isEvent && marketPreview.markets && marketPreview.markets.length > 0 ? (
                            <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
                              {marketPreview.markets.slice(0, 3).map((m, i) => {
                                const yes = parseFloat(m.outcomePrices?.[0] || "0") * 100;
                                return (
                                  <div key={i} className="flex justify-between gap-2">
                                    <span className="truncate">{m.question}</span>
                                    <span className="text-emerald-500 font-semibold shrink-0">{yes.toFixed(0)}¢</span>
                                  </div>
                                );
                              })}
                              {marketPreview.markets.length > 3 && (
                                <span className="text-[9px] text-gray-400 dark:text-gray-500">
                                  +{marketPreview.markets.length - 3} more
                                </span>
                              )}
                              <div className="text-[10px] text-gray-500 pt-0.5">
                                {formatVolume(marketPreview.volume)} vol
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 mt-1">
                              {marketPreview.outcomes && marketPreview.outcomePrices && (
                                <span className="text-[10px] font-bold text-green-600">
                                  YES {formatCents(parseFloat(marketPreview.outcomePrices?.[0] || "0"))}
                                </span>
                              )}
                              <span className="text-[10px] text-gray-500">
                                {formatVolume(marketPreview.volume)} vol
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer with counter and post button */}
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-[10px] text-gray-400">
                      {content.length}/500
                    </span>
                    <button
                      ref={postButtonRef}
                      type="submit"
                      disabled={!content.trim() || posting || !!urlError}
                      className="flex items-center gap-1.5 px-5 py-2.5 bg-[#7C3AED] dark:bg-[#CCFF00] text-white dark:text-[#0A0A0A] text-sm font-bold rounded-[5px] hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiSend className="w-3.5 h-3.5" />
                      {posting ? "..." : t("createPost.postButton")}
                    </button>
                  </div>
                </div>
              </form>
              </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
